import { useMemo } from "react";
import { ScrollView } from "react-native";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { EditableSiteName } from "@src/components/EditableSiteName";
import { UpcomingExceptions } from "@src/features/alarms/components/UpcomingExceptions";
import { ManageOptionsList, type ManageOption } from "@src/features/alarms/components/ManageOptionsList";
import { useSiteCameraKeys } from "@src/features/cameras/hooks/useSiteCameraKeys";
import { useSiteNames } from "@src/features/cameras/hooks/useSiteNames";
import { useSitePermissions } from "@visioni/a3s-access/app";
import { buildAlarmSettingsRoutes } from "@src/features/alarms/utils/alarm-settings-routes";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";

export default function SiteAlarmSettingsPage() {
  const { theme } = useUnistyles();
  const router = useRouter();
  const { tenantId, siteId } = useLocalSearchParams<{ tenantId: string; siteId: string }>();
  const { cameraKeys } = useSiteCameraKeys(siteId);
  const siteNames = useSiteNames();
  const siteName = siteNames?.get(siteId);
  const { canManageAllAlarmSchedules, isLoading: permissionsLoading } = useSitePermissions(siteId, cameraKeys);
  const { headerLeft } = useHeaderBackButton();

  const routes = useMemo(() => buildAlarmSettingsRoutes(tenantId, siteId), [tenantId, siteId]);

  const canManage = !permissionsLoading && canManageAllAlarmSchedules;

  const manageOptions: ManageOption[] = useMemo(
    () => [
      {
        id: "armDisarm",
        title: "Arm / Disarm site",
        description: "Temporarily arm or disarm all cameras in this site.",
        icon: "lockClosed",
        disabled: !canManage,
        onPress: () => router.push(routes.overrideSite as never),
      },
      {
        id: "routines",
        title: "Routines",
        description: "Manage recurring arming and disarming schedules.",
        icon: "calendarRefresh",
        disabled: !canManage,
        onPress: () => router.push(routes.manageRoutines as never),
      },
      {
        id: "exceptions",
        title: "Exceptions",
        description: "Schedule temporary overrides for holidays or maintenance.",
        icon: "calendarEvent",
        disabled: !canManage,
        onPress: () => router.push(routes.manageExceptions as never),
      },
      {
        id: "response",
        title: "Alarm response",
        description: "Configure sirens and strobes.",
        icon: "sirenDown",
        disabled: !canManage,
        onPress: () => router.push(routes.manageAlarmResponse as never),
      },
    ],
    [router, routes, canManage],
  );

  return (
    <>
      <Stack.Screen options={{ title: siteName ?? siteId, headerLeft }} />
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        contentContainerStyle={{
          paddingHorizontal: theme.spacing["3xl"],
          flexGrow: 1,
          paddingTop: theme.spacing["4xl"],
          paddingBottom: theme.spacing["12xl"],
        }}
      >
        <View gap="7xl">
          <View gap="xxs">
            {siteName ?
              <EditableSiteName siteId={siteId} currentName={siteName} />
            : <Text textStyle="lg/medium">{siteId}</Text>}
            <Text textStyle="sm/regular" color="textMuted">
              {siteId}
            </Text>
          </View>
          <ManageOptionsList options={manageOptions} />
          <UpcomingExceptions siteId={siteId} cameraKeys={cameraKeys} />
        </View>
      </ScrollView>
    </>
  );
}
